import { OrganizationType } from 'modules/Common/utils/Utility';
import { trackPreload } from 'modules/Common/utils/chunkLoadState';

// Page chunks for the routes people open most, keyed by the path the sidebar
// links to. _routes.jsx builds its lazy components from these same factories,
// so a preload and the later navigation share one import(): the browser's
// module map dedupes the fetch, and React.lazy resolves without a round trip.
//
// Only page chunks belong here. The heavy vendor families (pdf, editor, sheets,
// flow) stay behind their own seams: evaluating them at idle would itself be a
// long task on the main thread.
//
// ADDING A PAGE HERE? `vite.config.js` keeps its own `server.warmup.clientFiles`
// list that overlaps this one. It cannot import this module (the alias imports
// above do not resolve in the config's Node context), so the two are kept in
// sync by hand: add the page's file there too, or dev keeps paying the
// first-navigation transform this list exists to avoid.
//
// See docs/features/react-lazy-loading/phase-7-navigation-speed.md.
export const pageChunks = {
  '/overview': () => import('modules/Declarant/pages/DeclarantOverviewPage'),
  '/upload-hub': () => import('modules/UploadHub/pages/UploadHubPage'),
  '/suppliers': () => import('modules/Suppliers/pages/SuppliersSearchPage'),
  '/installations': () => import('modules/Installations/pages/InstallationsPage'),
  '/emissions': () => import('modules/Emissions/pages/EmissionsDataPage'),
  '/analytics-overview': () => import('modules/Analytics/pages/AnalyticsOverviewRoute'),
  '/reports': () => import('modules/Reports/pages/ReportPage'),
  '/operator-overview': () => import('modules/Operator/pages/OperatorOverviewPage'),
  '/operator-installations': () =>
    import('modules/OperatorInstallations/pages/OperatorInstallationsPage'),
  '/consultant-overview': () => import('modules/Consultant/pages/ConsultantOverviewPage'),
  '/verifier': () => import('modules/Verifier/pages/VerifierHomePage'),
};

// What each org type warms in the background once its workspace is up, most
// visited first. A page the user cannot open only costs its bytes: the chunk is
// fetched and evaluated, never rendered.
//
// Keep a page off this list if its static closure reaches a heavy vendor chunk
// (pdf, sheets, flow, editor): evaluating that at idle is exactly the long task
// this avoids. /reports qualifies again now that jszip has its own chunk.
export const idlePreloadPaths = {
  [OrganizationType.Declarant]: [
    '/overview',
    '/suppliers',
    '/installations',
    '/emissions',
    '/upload-hub',
    '/reports',
    '/analytics-overview',
  ],
  [OrganizationType.Operator]: ['/operator-overview', '/operator-installations'],
  [OrganizationType.Consultant]: ['/consultant-overview'],
  [OrganizationType.Verifier]: ['/verifier'],
};

// Best-effort: a failed preload is not an error. The navigation that needs the
// chunk retries through lazyWithRetry and reports there if it really is gone.
//
// On Chromium a module fetch that fails is recorded in the module map for the
// rest of the document, so a preload that hits a transient blip makes the later
// navigation to that page fail too. That costs one reload (src/index.jsx),
// not a broken page.
export const preloadPageChunk = (path) => {
  const importer = pageChunks[path];
  return importer ? trackPreload(importer()).catch(() => {}) : undefined;
};
